import { ChevronRight } from 'assets/icons/ChevronRight';
import User from 'assets/icons/User';
import BaseButton from 'components/ui/BaseButton';
import DropdownMenu from 'components/ui/DropdownMenu';
import { LanguageCode, THEME_OPTION } from 'enums';
import { LANGUAGE, MODAL_TYPES } from 'constants/general';
import {
  PROFILE_LANGUAGE_MENU_ID,
  PROFILE_SUBMENU_ID,
  PROFILE_THEME_MENU_ID
} from 'constants/navBar';
import { ROUTES } from 'constants/routes';
import { MenuItem } from 'interfaces';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { changeLanguage, t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { updateConfirmActionModal } from 'store/reducer/modal/slice';
import { sideNavAccountSelector } from 'store/reducer/setting/selectors';
import {
  updateIsShowPrivacyPolicyModal,
  updateLanguage,
  updateThemeMode
} from 'store/reducer/setting/slice';

interface SideNavAccountProps {
  profileSubMenus: MenuItem[];
  isCollapsed: boolean;
}

const SideNavAccount = ({
  profileSubMenus,
  isCollapsed
}: SideNavAccountProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userName, userEmail, themeMode, language } = useAppSelectorShallow(
    sideNavAccountSelector
  );

  const handleLanguageItemClick = useCallback(
    (menuItem: MenuItem) => {
      if (!menuItem.id) return;
      const languageCode =
        menuItem.id === PROFILE_LANGUAGE_MENU_ID.EN ? LANGUAGE.GB : LANGUAGE.ES;
      let code: LanguageCode = LanguageCode.EN;
      if (languageCode === LANGUAGE.ES) {
        code = LanguageCode.ES;
      }
      changeLanguage(code);
      dispatch(updateLanguage(code));
    },
    [dispatch]
  );

  const handleThemeItemClick = useCallback(
    (menuItem: MenuItem) => {
      if (!menuItem.id) return;
      const themeOption =
        menuItem.id === PROFILE_THEME_MENU_ID.LIGHT
          ? THEME_OPTION.LIGHT
          : THEME_OPTION.DARK;
      dispatch(updateThemeMode(themeOption));
    },
    [dispatch]
  );

  const handleProfileMenuClick = useCallback(
    (menuItem: MenuItem) => {
      if (
        Object.values(PROFILE_THEME_MENU_ID).includes(
          menuItem.id as (typeof PROFILE_THEME_MENU_ID)[keyof typeof PROFILE_THEME_MENU_ID]
        )
      ) {
        handleThemeItemClick(menuItem);
        return;
      }

      if (
        Object.values(PROFILE_LANGUAGE_MENU_ID).includes(
          menuItem.id as (typeof PROFILE_LANGUAGE_MENU_ID)[keyof typeof PROFILE_LANGUAGE_MENU_ID]
        )
      ) {
        handleLanguageItemClick(menuItem);
        return;
      }

      if (menuItem.id === PROFILE_SUBMENU_ID.PRIVACY_SECURITY) {
        dispatch(updateIsShowPrivacyPolicyModal(true));
        return;
      }

      if (menuItem.path === ROUTES.LOG_OUT) {
        dispatch(
          updateConfirmActionModal({
            modalType: MODAL_TYPES.CONFIRM_ACTION_LOGOUT,
            confirmTitle: t('logout'),
            description: t('are_you_sure_you_want_to_logout'),
            showConfirmModal: true
          })
        );
        return;
      }

      if (menuItem.path) {
        navigate(menuItem.path);
      }
    },
    [dispatch, navigate, handleLanguageItemClick, handleThemeItemClick]
  );

  const renderThemeMenuItem = useCallback(
    (menuItem: MenuItem) => {
      const isSelected =
        (menuItem.id === PROFILE_THEME_MENU_ID.LIGHT &&
          themeMode === THEME_OPTION.LIGHT) ||
        (menuItem.id === PROFILE_THEME_MENU_ID.DARK &&
          themeMode === THEME_OPTION.DARK);
      return (
        <div className='hover:bg-primary-100 dark:hover:bg-secondary-dark-300 rounded flex items-center justify-center ~gap-1.5/2 ~px-1/2.5 py-0.5'>
          <span
            className={`~text-[0.7rem]/xs ${
              isSelected
                ? 'font-semibold text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-secondary-200'
            }`}>
            {menuItem.text}
          </span>
        </div>
      );
    },
    [themeMode]
  );

  const renderLanguageMenuItem = useCallback(
    (menuItem: MenuItem) => {
      const isSelected = language === menuItem.id;
      return (
        <div className='hover:bg-primary-100 dark:hover:bg-secondary-dark-300 rounded flex items-center justify-center ~gap-1.5/2 ~px-1/2.5 py-0.5'>
          <span
            className={`~text-[0.7rem]/xs ${
              isSelected
                ? 'font-semibold text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-secondary-200'
            }`}>
            {menuItem.text}
          </span>
        </div>
      );
    },
    [language]
  );

  const renderSubMenuContentClassName = useCallback((menuItem: MenuItem) => {
    if (menuItem.id === PROFILE_SUBMENU_ID.LANGUAGE) {
      return 'flex flex-col !gap-0 !bg-secondary-50 dark:!bg-secondary-dark-200 !hover:bg-secondary-100 dark:!hover:bg-secondary-dark-100 shadow-lg rounded w-auto min-w-[5rem] z-[70]';
    }
    if (menuItem.id === PROFILE_SUBMENU_ID.THEME) {
      return 'flex flex-col !gap-0 !bg-secondary-50 dark:!bg-secondary-dark-200 !hover:bg-secondary-100 dark:!hover:bg-secondary-dark-100 shadow-lg rounded w-auto min-w-[8rem] z-[70]';
    }

    return 'flex flex-col gap-1 bg-secondary-50 dark:bg-secondary-dark-200 shadow-lg rounded w-full lg:w-56 p-2 z-[70]';
  }, []);

  const renderSubMenuStyle = useCallback((subPath?: string) => {
    const childPath = subPath && window.location.pathname.endsWith(subPath);
    return `~text-[0.7rem]/sm ~px-2/3 ~py-1/2 data-[state=open]:bg-primary-100 ${
      childPath
        ? 'bg-primary-200 hover:bg-primary-200 text-gray-900 hover:text-gray-900 dark:bg-secondary-dark-300 dark:hover:bg-secondary-dark-300 dark:text-white'
        : 'text-gray-700 dark:text-secondary-100 hover:bg-primary-100 dark:hover:bg-secondary-dark-300 bg-primary-50 dark:bg-secondary-dark-200'
    }`;
  }, []);

  return (
    <div className='border-t border-gray-200 dark:border-secondary-dark-300 ~p-2/3 w-full'>
      <DropdownMenu
        subMenus={profileSubMenus}
        onItemClick={handleProfileMenuClick}
        renderSubMenuStyle={renderSubMenuStyle}
        subTriggerIcon={<ChevronRight styles='ml-auto ~w-1/3 h-auto' />}
        renderMenuItem={(menuItem) => {
          if (
            Object.values(PROFILE_THEME_MENU_ID).includes(
              menuItem.id as (typeof PROFILE_THEME_MENU_ID)[keyof typeof PROFILE_THEME_MENU_ID]
            )
          ) {
            return renderThemeMenuItem(menuItem);
          }
          if (
            Object.values(PROFILE_LANGUAGE_MENU_ID).includes(
              menuItem.id as (typeof PROFILE_LANGUAGE_MENU_ID)[keyof typeof PROFILE_LANGUAGE_MENU_ID]
            )
          ) {
            return renderLanguageMenuItem(menuItem);
          }
          return undefined;
        }}
        renderSubMenuContentClassName={renderSubMenuContentClassName}
        contentClassName='shadow rounded-md mb-2'
        header={
          <div className='flex flex-col min-w-0 gap-0.5'>
            {userName ? (
              <span className='~text-xs/sm font-semibold text-primary-900 dark:text-white truncate'>
                {userName}
              </span>
            ) : null}
            <span className='truncate text-[0.7rem] font-normal text-gray-500 dark:text-secondary-200'>
              {userEmail}
            </span>
          </div>
        }>
        <BaseButton
          data-testid='side-nav-profile-btn'
          variant='icon'
          size='sm'
          additionalStyles={`text-gray-700 dark:text-secondary-100 ${
            isCollapsed ? 'mx-auto justify-center' : 'w-full justify-start'
          }`}>
          <span className='flex items-center ~gap-2/2.5 min-w-0 w-full'>
            <User
              className='~w-3.5/4 ~h-3.5/4 flex-shrink-0'
              fill='currentColor'
              fillDarkMode='currentColor'
            />
            {!isCollapsed ? (
              <span className='~text-xs/sm font-semibold truncate text-left'>
                {t('account')}
              </span>
            ) : null}
          </span>
        </BaseButton>
      </DropdownMenu>
    </div>
  );
};

export default SideNavAccount;
